import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, ShieldCheck, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { authApi } from '@/lib/api';
import { ThemeToggle } from '@/components/ui/theme-toggle';

/**
 * RAD Finance — Topbar
 * --------------------------------------------------------------------------
 * Barra superior do shell autenticado, no visual do design system RAD.
 *   - Esquerda: rótulo da plataforma.
 *   - Direita: ThemeToggle + menu de perfil (usuário + logout).
 *
 * Toda cor vem das CSS variables, então funciona em dark e light sem branches.
 */
export function Topbar() {
  return (
    <header className="h-16 border-b border-line bg-surface flex items-center gap-4 px-5">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xs uppercase tracking-[0.18em] text-faint hidden lg:inline">
          Gestão Financeira
        </span>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <ThemeToggle />
        <ProfileMenu />
      </div>
    </header>
  );
}

function ProfileMenu() {
  const navigate = useNavigate();
  const { user, tokens, clear } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  if (!user) return null;

  async function handleLogout() {
    setSigningOut(true);
    try {
      if (tokens?.refreshToken) await authApi.logout(tokens.refreshToken).catch(() => undefined);
    } finally {
      clear();
      navigate('/login', { replace: true });
    }
  }

  const roleLabel = user.role === 'ADMIN' ? 'Administrador' : 'Operador';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          'flex items-center gap-2 h-9 pl-1.5 pr-2 rounded-md',
          'text-subtle hover:text-fg hover:bg-surface-2',
          'transition-colors duration-fast ease-rad',
          'focus:outline-none focus-visible:shadow-rad-focus',
        )}
      >
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-pill bg-primary-500 text-white shrink-0">
          <UserRound className="w-4 h-4" aria-hidden />
        </span>
        <span className="hidden sm:block max-w-[10rem] truncate text-sm text-fg">{user.name}</span>
        <ChevronDown className="w-3.5 h-3.5 text-faint" aria-hidden />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute right-0 mt-2 w-60 z-50 rounded-lg border border-line bg-surface shadow-rad-lg overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-line">
              <div className="text-xs text-faint">{roleLabel}</div>
              <div className="text-sm font-medium text-fg truncate">{user.name}</div>
              <div className="text-xs text-subtle truncate">{user.email}</div>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/configuracoes/seguranca');
              }}
              className={cn(
                'w-full flex items-center gap-2 px-4 py-2.5 text-sm text-subtle',
                'hover:bg-surface-2 hover:text-fg transition-colors duration-fast ease-rad',
              )}
            >
              <ShieldCheck className="w-4 h-4" aria-hidden />
              <span>Segurança</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              disabled={signingOut}
              className={cn(
                'w-full flex items-center gap-2 px-4 py-2.5 text-sm text-subtle',
                'hover:bg-surface-2 hover:text-danger transition-colors duration-fast ease-rad',
                'disabled:opacity-60 disabled:pointer-events-none',
              )}
            >
              <LogOut className="w-4 h-4" aria-hidden />
              <span>{signingOut ? 'Saindo…' : 'Sair'}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
